import {Injectable, Renderer2, RendererFactory2, inject, DOCUMENT, effect, EffectRef, signal} from '@angular/core';
import {ApiService} from "./api.service";
import {BehaviorSubject, catchError, Observable, take, tap} from "rxjs";
import {ToasterService} from './toaster.service';
import {LanguageService} from './language.service';
import {SettingsAppearanceRequest} from '../types/settings-types/settingsAppearanceRequest';
import {CustomerService} from './customer.service';
import {SettingsTheme} from '../types/settings-types/settingsTheme';
import {CustomerAppearanceResource} from '../types/settings-types/customerAppearanceResource';
import {WritableSignal} from '@angular/core';
import {LocalStorageService} from './local-storage.service';

export type ThemeMode = 'light' | 'dark';

export type ThemeName =
  | 'original'
  | 'dark-blue' | 'sky-blue' | 'blue' | 'aqua' | 'cyan'
  | 'plain-yellow' | 'bright-green' | 'dark-green' | 'yellow'
  | 'orange' | 'dark-orange' | 'light-purple' | 'red'
  | 'purple' | 'pink' | 'lavender-blue' | 'asphalt';

interface ThemeMeta { label: string;}

@Injectable({
  providedIn: 'root'
})

export class LayoutService {
  private rendererFactory: RendererFactory2 = inject(RendererFactory2);
  private document: Document = inject<Document>(DOCUMENT);
  private apiService: ApiService = inject(ApiService);
  private toastService: ToasterService = inject(ToasterService);
  private language: LanguageService = inject(LanguageService);
  private customerService: CustomerService = inject(CustomerService);
  private localStorage: LocalStorageService = inject(LocalStorageService);

  public breakpointWidthType: BehaviorSubject<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'> = new BehaviorSubject<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'>('xs');
  public scrolledToEnd: BehaviorSubject<boolean | string> = new BehaviorSubject<boolean | string>(false);
  public scrolledToStart: BehaviorSubject<boolean | string> = new BehaviorSubject<boolean | string>(false);
  public isScrollable: BehaviorSubject<{containerId: string, scrollable: boolean} | null> = new BehaviorSubject<{containerId: string, scrollable: boolean} | null>(null);
  private renderer!: Renderer2;
  private backgroundImage: string | null = null;
  //private backgroundImageDefault: string = '/assets/images/backgrounds/default_light.png';
  private themeLink: HTMLLinkElement | null = null;
  // MODE = light/dark
  private readonly defaultThemeName: ThemeName = 'original';
  private readonly defaultBackgroundEnabled: boolean = true;
  private theme: ThemeMode = this.getBrowserDefaultTheme();

  // NAME = brand set
  private themeName: ThemeName = this.defaultThemeName;

  private themeMap: Record<ThemeMode, string> = {light: 'light-theme.css', dark: 'dark-theme.css'} as const;
  private readonly backgroundEnabled: WritableSignal<boolean> = signal(this.defaultBackgroundEnabled);
  private themeLoadEffect!: EffectRef;


  //FOR THE PHOTOSHOP landing.psd background creation to know the colors
  /*
  C is the primary, B is a slight shift from priamry, A is a very light tint of primary

  "original":      { A:"#EEF2FF", B:"#CFD7F9", C:"#3E55B1" }, 
  "dark-blue":     { A:"#E7F2FF", B:"#A6C8FF", C:"#2380B9" },
  "sky-blue":      { A:"#EEF9FF", B:"#A9F0FF", C:"#2AA8FF" },
  "blue":          { A:"#F0EDFF", B:"#D2C8FF", C:"#6F6BFF" },
  "aqua":          { A:"#E8FCF8", B:"#B6F3E8", C:"#00BDB3" },
  "cyan":          { A:"#E9FBFF", B:"#BFF1FB", C:"#3ED4F6" },
  "bright-green":  { A:"#F0FBEC", B:"#D0F2B5", C:"#92E653" },
  "dark-green":    { A:"#E9F8F4", B:"#BFEADE", C:"#1FAB8C" },
  "plain-yellow":  { A:"#FAFBDC", B:"#E8F573", C:"#DFDF07" },
  "yellow":        { A:"#FFF9EC", B:"#FFE1A3", C:"#FAB856" },
  "orange":        { A:"#FFF3EC", B:"#FFC7A6", C:"#FF9066" },
  "dark-orange":   { A:"#FFF4E6", B:"#FFD08A", C:"#FE870B" },
  "red":           { A:"#FFE9E6", B:"#FFB3A8", C:"#EC2D13" },
  "light-purple":  { A:"#F7EFFF", B:"#E2C9FD", C:"#B16FEF" },
  "purple":        { A:"#FBF0FF", B:"#E6C3FB", C:"#D67AF1" },
  "pink":          { A:"#FFF0F5", B:"#FFC1D4", C:"#FF769F" },
  "lavender-blue": { A:"#F1F3FF", B:"#C9D2FA", C:"#899CEA" },
  "asphalt":       { A:"#EEF3F7", B:"#C7D6E3", C:"#577490" },

  */

  private themeBackgrounds: Record<ThemeName, ThemeMeta> = {
    'original': {label: 'Original'},
    'dark-blue': {label: 'Ocean'},
    'sky-blue': {label: 'Sky'},
    'blue': {label: 'Indigo'},
    'aqua': {label: 'Aqua'},
    'cyan': {label: 'Glacier'},
    'bright-green': {label: 'Lime'},
    'dark-green': {label: 'Emerald'},
    'plain-yellow': {label: 'Volt'},
    'yellow': {label: 'Sunflower'},
    'orange': {label: 'Tangerine'},
    'dark-orange': {label: 'Amber'},
    'red': {label: 'Scarlet'},
    'light-purple': {label: 'Lilac'},
    'purple': {label: 'Orchid'},
    'pink': {label: 'Rose'},
    'lavender-blue': {label: 'Lavender'},
    'asphalt': {label: 'Slate'},
  };

  private darkmodeQuery: MediaQueryList = window.matchMedia?.('(prefers-color-scheme: dark)');

  public getBrowserDefaultTheme(): ThemeMode {
    return (this.darkmodeQuery?.matches ? 'dark' : 'light');
  }

  public constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.setPreConnect(this.apiService.getUrl());

    // initialize attributes before any DOM styling
    this.renderer.setAttribute(this.document.body, 'data-theme', this.themeName);
    this.renderer.setAttribute(this.document.body, 'data-bs-theme', this.theme);

    //reactive background toggle
    effect(() => {
      if (this.backgroundEnabled()) {
        this.setBackground();
      } else {
        this.clearBackground();
      }
    });

    // loads Syncfusion CSS & sets data-bs-theme again
    this.applyTheme(this.themeName, this.theme);

    this.themeLoadEffect = effect(() => {
      const appearance = this.customerService.publicCustomerResource()?.appearance;
      if(appearance) {
        this.localStorage.setLocalStorage('appearanceCache', JSON.stringify(appearance))
        this.applyTheme(
          appearance.color === null ? this.defaultThemeName : appearance.color as ThemeName,
          appearance.theme === SettingsTheme.Auto ?
            this.getBrowserDefaultTheme() :
            appearance.theme === SettingsTheme.Light ? 'light' : 'dark'
        );
        this.backgroundEnabled.set(appearance.background);
      } else {
        const cachedConfig = this.localStorage.getLocalStorage('appearanceCache');
        if(cachedConfig !== null) {
          try {
            const parsedConfig = JSON.parse(cachedConfig) as Partial<CustomerAppearanceResource>;
            const isValidTheme = parsedConfig.theme === SettingsTheme.Auto
              || parsedConfig.theme === SettingsTheme.Light
              || parsedConfig.theme === SettingsTheme.Dark;
            const isValidColor = parsedConfig.color === null
              || (typeof parsedConfig.color === 'string'
                && Object.prototype.hasOwnProperty.call(this.themeBackgrounds, parsedConfig.color));
            const isValidBackground = typeof parsedConfig.background === 'boolean';

            if (isValidTheme && isValidColor && isValidBackground) {
              this.applyTheme(
                parsedConfig.color === null ? this.defaultThemeName : parsedConfig.color as ThemeName,
                parsedConfig.theme === SettingsTheme.Auto
                  ? this.getBrowserDefaultTheme()
                  : parsedConfig.theme === SettingsTheme.Light ? 'light' : 'dark'
              );
              
              this.applyTheme(
                parsedConfig.color === null ? this.defaultThemeName : parsedConfig.color as ThemeName,
                parsedConfig.theme === SettingsTheme.Auto ?
                  this.getBrowserDefaultTheme() :
                  parsedConfig.theme === SettingsTheme.Light ? 'light' : 'dark'
              );
              this.backgroundEnabled.set(parsedConfig.background!);
            } else {
              this.resetTheme();
            }
          } catch {
            this.localStorage.removeItem('appearanceCache');
            this.resetTheme();
          }
        }
      }
    });

    this.setEventListeners();
    this.detectAndApplyOsThemeChange();
  }

  public applyTheme(name: ThemeName, mode: ThemeMode): void {
    const nameChanged = this.themeName !== name;
    const modeChanged = (this.theme !== mode) || !this.themeLink; // first load forces run
    if (nameChanged) this.setThemeName(name);
    if (modeChanged) this.setTheme(mode);
  }

  public resetTheme(): void {
    this.applyTheme(this.defaultThemeName, this.getBrowserDefaultTheme());
    this.backgroundEnabled.set(this.defaultBackgroundEnabled);
  }

  // set brand theme name (switches CSS vars from SCSS, and background)
  public setThemeName(name: ThemeName): void {
    name = (name === null) ? 'original' : name; // fallback to original if null
    if (this.themeName === name) return;
    this.themeName = name;
    this.renderer.setAttribute(this.document.body, 'data-theme', name);
    this.setBackgroundByTheme();

    const base = getComputedStyle(this.document.body).getPropertyValue('--our-title-color').trim();
    this.applyColorShades(base);
  }

  public getThemeName(): ThemeName { return this.themeName; }

  public setBackground(): void {
    //this.backgroundImage = imageUrl ? imageUrl : this.backgroundImageDefault;
    //this.renderer.setStyle(this.document.body, 'background-image', 'url(' + this.backgroundImage + ')');
    this.renderer.setStyle(this.document.body, 'background', 'var(--our-theme-preview-bubble-background), var(--our-background-color)');
  }

  public clearBackground(): void {
    this.renderer.removeStyle(this.document.body, 'background');
  }

  public getBackground(): string | null {
    return this.backgroundImage;
  }

  private setPreConnect(url: string): void {
    const link = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'rel', 'preconnect');
    this.renderer.setAttribute(link, 'href', url);
    this.renderer.appendChild(this.document.head, link);
  }

  // Method to load Syncfusion CSS into the <style> tag and set Bootstrap theme
  public setTheme(mode: ThemeMode): void {
    const firstLoad = !this.themeLink;
    const sameMode = this.theme === mode;
    if (sameMode && !firstLoad) return;   // allow first load even if same

    this.theme = mode;

    if (this.themeLink) {
      this.renderer.setAttribute(this.themeLink, 'href', this.themeMap[mode]);
    } else {
      const themeLink: HTMLLinkElement = this.renderer.createElement('link');
      this.renderer.setAttribute(themeLink, 'rel', 'stylesheet');
      this.renderer.setAttribute(themeLink, 'href', this.themeMap[mode]);

      // Find the 'styles.css' link element
      const stylesLink = this.document.querySelector('link[href="styles.css"]');

      if (stylesLink) {
        const next = stylesLink.nextSibling;
        if (next) {
          this.renderer.insertBefore(this.document.head, themeLink, next);
        } else {
          this.renderer.appendChild(this.document.head, themeLink);
        }
      } else {
        // If 'styles.css' isn't found, append the new theme link at the end
        this.renderer.appendChild(this.document.head, themeLink);
      }

      // Set the reference for the theme link
      this.themeLink = themeLink;
    }

    // Set data-bs-theme attribute on the body tag for Bootstrap theme
    this.renderer.setAttribute(this.document.body, 'data-bs-theme', mode);

    // update background for this mode
    this.setBackgroundByTheme();

    // when the Syncfusion css finished loading, recompute shades from current primary: generate --our-shade-* from --our-title-color
    this.setShades();
  }

  //generates the shades which Syncfusion is using. The shades are generated from the --our-title-color which is the primary color
  private setShades(): void {
    if (this.themeLink) {
      const link = this.themeLink;
      const run = () => {
        const bodyStyles = getComputedStyle(this.document.body);
        if(bodyStyles) {
          const base = bodyStyles.getPropertyValue('--our-title-color').trim();
          if (base) {
            this.applyColorShades(base);
          }
        }
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((link as any).readyState === 'complete') run();
      else link.onload = run;
    } else {
      // in case themeLink is not yet set (shouldn’t happen in your flow)
      const base = getComputedStyle(this.document.body).getPropertyValue('--our-title-color').trim();
      if (base) this.applyColorShades(base);
    }
  }

  //method to change the primary color in the entire website
  public setBaseColor(newColor: string): void {
    const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
    if (hexPattern.test(newColor)) {
      this.document.body.style.setProperty('--our-title-color', newColor);
      this.applyColorShades(newColor);
    }
  }

  // Method to get the current theme
  public getTheme(): ThemeMode { return this.theme; }

  public getThemeCatalog(): readonly { name: ThemeName; label: string;}[] {
    return (Object.keys(this.themeBackgrounds) as ThemeName[]).map(name => ({
      name,
      label: this.themeBackgrounds[name].label,
      //bg: this.themeBackgrounds[name].bg
    }));
  }

  // Method to toggle between light and dark themes
  public toggleTheme(): void { this.setTheme(this.theme === 'light' ? 'dark' : 'light'); }

  private setBackgroundByTheme(): void {
    if (!this.backgroundEnabled()) {
      this.clearBackground();
      return;
    }

    this.setBackground();
  }

  public setBackgroundEnabled(enabled: boolean): void {
    this.backgroundEnabled.set(enabled);
  }

  public isBackgroundEnabled(): boolean {
    return this.backgroundEnabled();
  }

  public getLogoByTheme(): string {
    return this.getTheme() === 'dark' ? '/assets/images/logos/humanstarsFullWhite.png' : '/assets/images/logos/humanstarsFullGrey.png'
  }

  //Resolves CSS variable like `var(--our-error-red)` to actual computed color
  public resolveCSSVariable(cssVar: string): string {
    const dummy = document.createElement('div');
    dummy.style.display = 'none';
    dummy.style.color = cssVar;
    this.document.body.appendChild(dummy);
    const computedColor = getComputedStyle(dummy).color; // e.g., rgb(255, 0, 0)
    this.document.body.removeChild(dummy);
    return computedColor;
  }

  //Converts rgb/rgba to rgba with optional alpha
  public toRGBA(color: string, alpha: number): string {
    const match = color.match(/\d+/g);
    return match ? `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})` : color;
  }

  // Darkens or lightens the given color, if you add positive value or a negative
  private adjustLightness(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * Math.abs(percent));
    const R = (num >> 16) + (percent > 0 ? amt : -amt);
    const G = (num >> 8 & 0x00FF) + (percent > 0 ? amt : -amt);
    const B = (num & 0x0000FF) + (percent > 0 ? amt : -amt);

    return `#${(
      0x1000000 +
      (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 0 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)}`;
  }

  private applyColorShades(baseColor: string) {
    const body = this.document.body;

    // Calculate and set the shades
    // Set color shades for the body element
    body.style.setProperty('--our-shade-900', this.adjustLightness(baseColor, 40));
    body.style.setProperty('--our-shade-800', this.adjustLightness(baseColor, 30));
    body.style.setProperty('--our-shade-700', this.adjustLightness(baseColor, 20));
    body.style.setProperty('--our-shade-600', this.adjustLightness(baseColor, 10));
    body.style.setProperty('--our-shade-500', baseColor);
    body.style.setProperty('--our-shade-400', this.adjustLightness(baseColor, -5));
    body.style.setProperty('--our-shade-300', this.adjustLightness(baseColor, -10));
    body.style.setProperty('--our-shade-200', this.adjustLightness(baseColor, -15));
    body.style.setProperty('--our-shade-100', this.adjustLightness(baseColor, -20));
  }

  private setEventListeners(): void {
    this.setBreakPointWidthType();
    window.addEventListener('resize', this.setBreakPointWidthType.bind(this));
  }

  private setBreakPointWidthType(): void {
    const width = window.innerWidth;
    let nextBreakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

    switch (true) {
      case width < 576:
        nextBreakpoint = 'xs';
        break;
      case width >= 576 && width < 768:
        nextBreakpoint = 'sm';
        break;
      case width >= 768 && width < 992:
        nextBreakpoint = 'md';
        break;
      case width >= 992 && width < 1200:
        nextBreakpoint = 'lg';
        break;
      case width >= 1200 && width < 1400:
        nextBreakpoint = 'xl';
        break;
      default:
        nextBreakpoint = 'xxl';
        break;
    }

    if (this.breakpointWidthType.value !== nextBreakpoint) {
      this.breakpointWidthType.next(nextBreakpoint);
    }
  }

  public setScrollToEnd(event: Event, container: string): void {
    const element = event.target as HTMLElement;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 1;
    if (atBottom) {
      this.scrolledToEnd.next(container !== '' ? container : atBottom);
    }
  }

  public setScrollToStart(event: Event, container: string): void {
    const element = event.target as HTMLElement;
    const atTop = element.scrollTop === 0;
    if (atTop) {
      this.scrolledToStart.next(container !== '' ? container : atTop);
    }
  }

  /**
   * Checks if an element is scrollable (has overflow content)
   * @param elementOrId - HTML element or string ID of the element
   * @param containerId - Optional identifier for tracking which container was checked
   * @returns boolean indicating if the element is scrollable
   */
  public checkIsScrollable(elementOrId: HTMLElement | string, containerId?: string): boolean {
    const element = typeof elementOrId === 'string' 
      ? this.document.getElementById(elementOrId) 
      : elementOrId;
    if (!element) {
      console.warn('Element not found for scrollability check:', elementOrId);
      return false;
    }
    const isScrollable = element.scrollHeight > element.clientHeight;
    if (containerId) {
      this.isScrollable.next({containerId, scrollable: isScrollable});
    }
    return isScrollable;
  }

  private isOsThemeChangeAllowed(): boolean {
    const appearance = this.customerService.publicCustomerResource()?.appearance;
    if (appearance) {
      return appearance.theme === SettingsTheme.Auto;
    }

    const cachedConfig = this.localStorage.getLocalStorage('appearanceCache');
    if (cachedConfig !== null) {
      try {
        const parsedConfig = JSON.parse(cachedConfig) as Partial<CustomerAppearanceResource>;
        return parsedConfig.theme === SettingsTheme.Auto;
      } catch {
        return false;
      }
    }

    return false;
  }

  private detectAndApplyOsThemeChange(): void {
    this.darkmodeQuery.addEventListener('change', () => {
      if (this.isOsThemeChangeAllowed()) {
        this.setTheme((this.darkmodeQuery?.matches ? 'dark' : 'light'));
      }
    });
  }

  //#region theme api connection
  public saveTheme$(data: SettingsAppearanceRequest): Observable<CustomerAppearanceResource> {
    return this.apiService.setAppearance(data).pipe(
      take(1),
      tap((res: CustomerAppearanceResource) => {
        this.toastService.success(
          this.language.getTranslationByKey('SETTHEMESUCCESSTITLE'),
          this.language.getTranslationByKey('SETTHEMESUCCESSMESSAGE')
        );

        if (res) {
          this.applyTheme(
            (res.color ?? 'original') as ThemeName,
            res.theme === SettingsTheme.Auto
              ? this.getBrowserDefaultTheme()
              : res.theme === SettingsTheme.Light
              ? 'light'
              : 'dark'
          );
        }
      }),
      catchError((err) => {
        console.error(err);
        return new Observable<CustomerAppearanceResource>();
      })
    );
  }
    

    public loadTheme (): Observable<CustomerAppearanceResource> {
      return this.apiService.getAppearance().pipe(
        take(1),
        catchError((err) => {
          console.error(err);
          return new Observable<CustomerAppearanceResource>(); // Return an empty observable on error
        })
      );
    }
  //#endregion theme api connection
}
